import { useHistory } from 'react-router-dom';
import {
  IconEncircledCheckmark,
  IconEncircledX,
  IconTimeClock,
  IconWarning
} from '../../../../../../assets/DesignSystem/SVGComponents';
import {
  PAYMENT_METHODS_THAT_REQUIRE_A_QUOTE_UPLOAD,
  PAYMENT_METHODS_THAT_SHOW_LINK_AUTOMATED_PAYMENT
} from '../../../../../../constants/constants';
import {
  IlogisticServicePaymentData,
  IQuotesLog,
  LIST_OF_QUOTE_STATUSES,
  LOGISTIC_SERVICE_STATUS_NAME
} from '../../../../../../types/services';
import { formatShortDate } from '../../../../../../utils/formatDatesAndHours';
import './LogisticServicePaymentInformation.scss';

const LogisticServicePaymentInformation = ({
  id,
  paymentData,
  status,
  quoteDocumentUrl,
  openPaymentLinkModalCallback,
  proofOfPaymentUrl,
  manualQuote,
  quotesLog,
  isLogisticDetailsView = false
}: {
  status?: string | null;
  paymentData?: IlogisticServicePaymentData | null;
  quoteDocumentUrl?: string | null;
  proofOfPaymentUrl?: string | null;
  openPaymentLinkModalCallback: () => void;
  manualQuote?: boolean;
  quotesLog?: IQuotesLog;
  isLogisticDetailsView?: boolean;
  id?: number | null;
}) => {
  const history = useHistory();

  const { isPaymentAgreement, paymentAgreementDate, isPaymentReceived, paymentType, paymentLink, paymentMethod } =
    paymentData || {};

  const quotesStatus = quotesLog?.status;
  const isPayedWithNudosCare = paymentType === 'Care';
  const isCanceled = status === LOGISTIC_SERVICE_STATUS_NAME.CANCELLED;
  const isDelivered = status === LOGISTIC_SERVICE_STATUS_NAME.DELIVERED;
  //manual states

  const pendingQuoteServiceManual =
    (quotesStatus === LIST_OF_QUOTE_STATUSES?.PENDING && quoteDocumentUrl && manualQuote) ||
    (!quotesStatus && quoteDocumentUrl && manualQuote);

  // es estado inicial cuando no tienen ningun estado cargado
  const emptyQuoteServiceManual = !quotesStatus && !isCanceled && !isDelivered && manualQuote && !quoteDocumentUrl;

  // metodos de pago que se necsitan para que se haga por medio del cargue de link
  const loadPaymentLinkValidMethods = paymentMethod
    ? PAYMENT_METHODS_THAT_SHOW_LINK_AUTOMATED_PAYMENT?.includes(paymentMethod)
    : undefined;

  // se establece el estado incial del cargue de un link para pago una vez que se cargo la cotizacion y esta ya se aprobo
  const loadPaymentLinkServiceManual =
    loadPaymentLinkValidMethods && quotesStatus === 'approved' && !paymentLink && !isPayedWithNudosCare;

  // metodos de pago que se nececitan para que se haga por cargue de cotizacion
  const uploadQuoteValidMethods = paymentMethod
    ? PAYMENT_METHODS_THAT_REQUIRE_A_QUOTE_UPLOAD?.includes(paymentMethod)
    : undefined;
  // se establece el estado incial del cargue de cotizacion
  const uploadQuoteServiceManual = emptyQuoteServiceManual;
  const paidQuoteServiceManual =
    // si el pago fue recibido y se hizo por cargue de cotizacion es aprovado
    ((isPaymentReceived && uploadQuoteValidMethods) || // si se hizo por link es aprobado cuando llega el link
      (loadPaymentLinkValidMethods && isPaymentReceived)) &&
    manualQuote;

  // esta aprobado pero no lo han pagado
  const approvedQuoteServiceManual =
    quotesStatus === LIST_OF_QUOTE_STATUSES?.APPROVED &&
    manualQuote &&
    !isPayedWithNudosCare &&
    !paidQuoteServiceManual;

  const showManualQuoteOptions = manualQuote && !!paymentData?.paymentMethod;
  //automated states

  const showAddPaymentLinkCallToAction =
    !paymentLink &&
    !isPayedWithNudosCare &&
    !isPaymentReceived &&
    !isDelivered &&
    !isCanceled &&
    loadPaymentLinkValidMethods;

  const showAddPayQuoteFileCallToAction =
    !isPayedWithNudosCare && !isDelivered && !isCanceled && !quoteDocumentUrl && uploadQuoteValidMethods;

  const isPayedWithNudosCareAndLogisticDetailsView = isLogisticDetailsView && isPayedWithNudosCare;

  const isPayedWithPaymentAgreement = isPaymentAgreement && isPaymentReceived;

  const isPayedWithNoPaymentAgreement = (isPaymentReceived || proofOfPaymentUrl) && !isPaymentAgreement;
  const isPaymentAgreementWithCreditBord = isPaymentAgreement && paymentMethod?.toLowerCase()?.includes('crédito bord');
  const isPaymentAgreementAndNotPayed = isPaymentAgreement && !isPaymentReceived && !isPaymentAgreementWithCreditBord;

  const redirectToPriceEdition = () => history.push(`/logistic-service/edit/${id}`);

  const getPaymentDisplayData = () => {
    if (showAddPayQuoteFileCallToAction)
      return {
        icon: <IconWarning className="IconWarning" />,
        text: 'Crear cotización',
        action: redirectToPriceEdition,
        classname: 'redirectToPriceEdition'
      };
    if (showAddPaymentLinkCallToAction)
      return {
        icon: <IconWarning className="IconWarning" />,
        text: 'Cargar link de pago',
        action: openPaymentLinkModalCallback,
        classname: 'openPaymentLinkModalButton'
      };
    if (isCanceled)
      return {
        icon: <IconEncircledX className="IconEncircledX" />,
        text: 'Cancelado',
        classname: 'red'
      };
    if (isPayedWithNudosCare)
      return {
        icon: <IconEncircledCheckmark className="IconEncircledCheckmark" />,
        text: 'Cubierto con Nudos Care',
        classname: 'green'
      };
    if (isPayedWithNoPaymentAgreement)
      return {
        icon: <IconEncircledCheckmark className="IconEncircledCheckmark" />,
        text: 'Pagado',
        classname: 'green'
      };
    if (isPaymentAgreementAndNotPayed)
      return {
        icon: <IconTimeClock className="IconTimeClock" />,
        text: paymentAgreementDate
          ? `Acuerdo para ${formatShortDate(paymentAgreementDate)?.onlyDate}`
          : 'Sin fecha de acuerdo',
        classname: 'blue'
      };

    if (isPayedWithPaymentAgreement)
      return {
        icon: <IconEncircledCheckmark className="IconEncircledCheckmark" />,
        text: paymentAgreementDate
          ? `Acuerdo para ${formatShortDate(paymentAgreementDate)?.onlyDate}`
          : 'Sin fecha de acuerdo',
        classname: 'green'
      };
    return { icon: <IconTimeClock className="IconTimeClock" />, text: 'Pendiente de pago', classname: '' };
  };

  const getManualQuotePaymentDisplayData = () => {
    if (isPayedWithNudosCare)
      return {
        icon: <IconEncircledCheckmark className="IconEncircledCheckmark" />,
        text: 'Cubierto con Nudos Care',
        classname: 'green'
      };

    if (uploadQuoteServiceManual)
      return {
        icon: <IconWarning className="IconWarning" />,
        text: 'Crear cotización',
        action: redirectToPriceEdition,
        classname: 'redirectToPriceEdition'
      };

    if (pendingQuoteServiceManual)
      return { icon: <IconTimeClock className="IconTimeClock" />, text: 'Pendiente de aprobación', classname: '' };

    if (loadPaymentLinkServiceManual)
      return {
        icon: <IconWarning className="IconWarning" />,
        text: 'Cargar link de pago',
        action: openPaymentLinkModalCallback,
        classname: 'openPaymentLinkModalButton'
      };

    if (isPaymentAgreementWithCreditBord)
       return {
         icon: <IconEncircledCheckmark className="IconEncircledCheckmark" />,
         text: paymentAgreementDate
           ? `Acuerdo para ${formatShortDate(paymentAgreementDate)?.onlyDate}`
           : 'Sin fecha de acuerdo',
         classname: 'blue'
       };

    if (approvedQuoteServiceManual)
      return { icon: <IconTimeClock className="IconTimeClock" />, text: 'Pendiente de pago', classname: '' };

    //JB: no se utiliza ?
    if (approvedQuoteServiceManual && loadPaymentLinkServiceManual)
      return {
        icon: <IconEncircledCheckmark className="IconEncircledCheckmark" />,
        text: 'Cotización aprobada',
        classname: 'green'
      };

    if (isCanceled)
      return {
        icon: <IconEncircledX className="IconEncircledX" />,
        text: 'Cancelado',
        classname: 'red'
      };

    if (paidQuoteServiceManual)
      return {
        icon: <IconEncircledCheckmark className="IconEncircledCheckmark" />,
        text: 'Pagado',
        classname: 'green'
      };

    return { icon: <IconTimeClock className="IconTimeClock" />, text: 'Pendiente de pago', classname: '' };
  };

  const displayData = showManualQuoteOptions ? getManualQuotePaymentDisplayData() : getPaymentDisplayData();

  const LogisticServicePaymentInformationStyles = `LogisticServicePaymentInformation ${
    isLogisticDetailsView ? 'logisticDetailsView' : ''
  }`;

  const paymentStatusContainerStyles = `paymentStatusContainer ${displayData.classname} ${
    isLogisticDetailsView ? 'logisticDetailsViewContainer' : ''
  }`;

  const casesToNotShowPaymentInformation = !displayData || isPayedWithNudosCareAndLogisticDetailsView;

  if (casesToNotShowPaymentInformation) return null;

  return (
    <div className={LogisticServicePaymentInformationStyles} onClick={displayData?.action}>
      {isLogisticDetailsView && <div className="paymentStatusTitle">Estado del pago:</div>}
      <div className={paymentStatusContainerStyles}>
        {displayData?.icon}
        <div className="text">{displayData.text}</div>
      </div>
    </div>
  );
};

export default LogisticServicePaymentInformation;
